import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import axiosInstance from '../../utils/axiosInstance';
import { Search, Menu, X, ShoppingBag } from 'lucide-react'; // Using Lucide as per the design

const Navbar = () => {
  const { isAuthenticated, isAdmin } = useContext(AuthContext);
  const navigate = useNavigate();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axiosInstance.get('/categories');
        // Sort by sequence before setting state
        setCategories(response.data.sort((a, b) => a.sequence - b.sequence));
      } catch (error) {
        console.error("Failed to fetch categories", error);
      }
    };
    fetchCategories();
  }, []);

  // --- SPLIT CATEGORIES FOR TWO NAVBARS ---
  const topNavCategories = categories.slice(0, 2);
  const subNavCategories = categories.slice(2);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${searchQuery}`);
      setSearchQuery('');
      setIsMobileMenuOpen(false);
    }
  };
  
  const handleLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
     {/* --- BANNER --- */}
      <div className="bg-[#551756] text-[#CAB273] text-center py-2 text-xs uppercase tracking-widest font-bold">
        No Extra Shipping cost.
      </div>

      {/* --- MAIN NAVIGATION --- */}
      <nav className="sticky top-0 z-50 bg-[#EBE6DE]/95 backdrop-blur-sm border-b border-[#551756]/10 font-sans text-[#551756]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            
            {/* 1. Logo */}
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="font-serif text-2xl font-bold tracking-widest text-[#551756]">
                Afrin Jewelry
              </Link>
            </div>

            {/* 2. Desktop Menu (Home + Top 2 Categories) */}
            <div className="hidden md:flex space-x-8 items-center">
              <Link to="/" className="text-sm uppercase tracking-widest hover:text-[#CAB273] transition-colors duration-300 font-medium">
                Home
              </Link>
              {topNavCategories.map(cat => (
                <Link 
                  key={cat.id} 
                  to={`/products?category=${cat.id}`} 
                  className="text-sm uppercase tracking-widest hover:text-[#CAB273] transition-colors duration-300 font-medium"
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* 3. Icons & Functional Area (Search, Auth, Mobile Toggle) */}
            <div className="flex items-center space-x-6">
              
              {/* Desktop Search Bar (Styled to blend in) */}
              <form onSubmit={handleSearch} className="hidden md:flex items-center relative group">
                <input 
                  type="text" 
                  value={searchQuery} 
                  onChange={(e) => setSearchQuery(e.target.value)} 
                  placeholder="Search..." 
                  className="bg-transparent border-b border-[#551756] text-[#551756] placeholder-[#551756]/50 px-2 py-1 focus:outline-none focus:border-[#CAB273] w-32 transition-all group-hover:w-48"
                />
                <button type="submit" className="ml-2 hover:text-[#CAB273] transition-colors">
                  <Search className="w-5 h-5" />
                </button>
              </form>

              {/* Shopping Bag (Visual Placeholder based on design) */}
              {/* <div className="relative cursor-pointer hover:text-[#CAB273] transition-colors">
                <ShoppingBag className="w-5 h-5" />
                <span className="absolute -top-2 -right-2 bg-[#CAB273] text-[#551756] text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">2</span>
              </div> */}

              {/* Auth Buttons */}
              {isAuthenticated && isAdmin && (
                 <div className="hidden md:flex items-center space-x-4">
                    <Link to="/admin" className="text-xs uppercase font-bold border border-[#551756] px-3 py-1 hover:bg-[#551756] hover:text-[#EBE6DE] transition-all">
                      Dashboard
                    </Link>
                    <Link to="/logout" className="text-xs uppercase font-bold text-[#551756] hover:text-red-600 transition-colors">
                      Log Out
                    </Link>
                 </div>
              )}

              {/* Mobile Menu Toggle */}
              <button className="md:hidden" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* --- MOBILE MENU DROPDOWN --- */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-[#EBE6DE] border-b border-[#551756]/10 pb-4">
            <div className="px-4 pt-2 pb-3 space-y-1">
              {/* Mobile Search */}
              <form onSubmit={handleSearch} className="flex items-center border border-[#551756]/20 p-2 mb-4 bg-white/50">
                  <input 
                    type="text" 
                    value={searchQuery} 
                    onChange={(e) => setSearchQuery(e.target.value)} 
                    placeholder="Search..." 
                    className="bg-transparent w-full focus:outline-none text-[#551756] placeholder-[#551756]/50"
                  />
                  <button type="submit"><Search className="w-5 h-5 text-[#551756]" /></button>
              </form>

              <Link to="/" className="block py-2 text-base font-medium hover:text-[#CAB273]" onClick={handleLinkClick}>Home</Link>
              
              <div className="py-2">
                <p className="text-xs uppercase tracking-widest text-[#CAB273] font-bold mb-2">Collections</p>
                {categories.map(cat => (
                  <Link key={cat.id} to={`/products?category=${cat.id}`} className="block py-2 pl-2 text-base font-medium hover:bg-[#551756]/5 hover:text-[#CAB273]" onClick={handleLinkClick}>
                    {cat.name}
                  </Link>
                ))}
              </div>

              {isAuthenticated && isAdmin && (
                <div className="border-t border-[#551756]/10 pt-4 mt-2">
                  <Link to="/admin" className="block py-2 text-base font-medium hover:text-[#CAB273]" onClick={handleLinkClick}>Dashboard</Link>
                  <Link to="/logout" className="block py-2 text-base font-medium text-red-700" onClick={handleLinkClick}>Log Out</Link>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* --- SUB-NAVBAR (Remaining Categories) --- */}
      {/* Visual Update: Plum background with Gold text to contrast the main cream navbar */}
      {subNavCategories.length > 0 && (
        <div className="hidden md:block bg-[#551756] text-[#CAB273] border-b border-[#CAB273]/20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
                <div className="flex flex-row flex-wrap justify-center gap-x-8 gap-y-2">
                    {subNavCategories.map(cat => (
                        <Link 
                          key={cat.id} 
                          to={`/products?category=${cat.id}`} 
                          className="text-xs uppercase tracking-widest font-bold hover:text-[#EBE6DE] transition-colors"
                        >
                          {cat.name}
                        </Link>
                    ))}
                </div>
            </div>
        </div>
      )}
    </>
  );
};

export default Navbar;